import Big from "big.js";
import CryptoJS from "crypto-js";
import { atom, useAtomValue, useSetAtom } from "jotai";

import { useCurrency } from "@/hooks/useCurrency";
import { useInterval } from "@/hooks/useInterval";
import { tonClientAtom, TonClientType } from "@/hooks/useTonstakers";
import { fromNano } from "@/utils/numbers";

export const earnApyAtom = atom<Record<string, Big | undefined>>({});

export const useEarnApy = () => {
  return useAtomValue(earnApyAtom);
};

function sha256Hash(message: string) {
  const hashHex = CryptoJS.SHA256(message).toString(CryptoJS.enc.Hex);
  return BigInt(`0x${hashHex}`);
}

const fetchEvaa = async (tonClient: TonClientType) => {
  const client = tonClient.tonton;

  const evaa = client.open(new tonClient.Evaa());

  await evaa.getSync();

  const assetsData = evaa.data?.assetsData!;

  const assetData = assetsData.get(sha256Hash("tsTON"));

  return assetData ? assetData.supplyApy * 100 : undefined;
};

const useApyUpdater = (key: string) => {
  const setEarnApy = useSetAtom(earnApyAtom);

  const setApy = (apy: Big) =>
    setEarnApy((old) => {
      const newValue = { ...old, [key]: apy };
      return newValue;
    });

  return setApy;
};

const useUpdateEvaaApy = () => {
  const setApy = useApyUpdater("evaa");
  const tonClient = useAtomValue(tonClientAtom);

  const updater = async () => {
    if (tonClient) {
      const apy = await fetchEvaa(tonClient);
      if (apy) {
        setApy(Big(apy));
      }
    }
  };

  useInterval(updater, 3600 * 1000, [tonClient], true);
};

const makeStonRequest = async (method: string) => {
  const response = await fetch(`https://api.ston.fi/v1/${method}`, {
    headers: { Accept: "application/json" },
  });

  return response.json();
};

const STONFI_POOL = "EQCdQZq6x_jrm4wAha4KUIVa0rtrz7avo6th9IWrIIa4RPSi";

const useUpdateStonApy = () => {
  const setApy = useApyUpdater("ston");

  return async () => {
    const poolData = await makeStonRequest(`pools/${STONFI_POOL}`);
    const poolApy = Big(poolData.pool.apy_30d);

    const farmData = await makeStonRequest(`farms_by_pool/${STONFI_POOL}`);
    const farmApy = Big(farmData.farm_list[0].apy);

    const fullApy = farmApy.add(poolApy);

    setApy((fullApy.gt(0) ? fullApy : Big(0)).mul(100));
  };
};

const DEDUST_POOL = "EQBkh7Mc411WTYF0o085MtwJpYpvGhZOMBphhIFzEpzlVODp";

const DEDUST_QUERY = ` query {
  pool(address: "${DEDUST_POOL}") {
    assets
    fees
    reserves
  }
  boosts {
    liquidityPool
    asset
    rewardPerDay
  }
}

`;
const useUpdateDedustApy = () => {
  const setApy = useApyUpdater("dedust");
  // const setBoostApy = useApyUpdater("dedustboost");
  const currency = useCurrency();

  return async () => {
    const response = await fetch("https://api.dedust.io/v3/graphql", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        query: DEDUST_QUERY,
      }),
    });

    const data = (await response.json()).data;

    const pool = data.pool;
    const boosts = (data.boosts as any[]).filter(
      (boost) => boost.liquidityPool === DEDUST_POOL,
    );

    boosts.push({ rewardPerDay: 0 }); // this is a default value to handle cases where no boost is present

    const fees: string[] = pool.fees;
    const reserves: string[] = pool.reserves;

    const calculateFee = (index: number) => {
      return Big(fees[index]).div(reserves[index]).mul(0.8);
    };

    const dpy = fees
      .reduce((prev, _, i) => prev.add(calculateFee(i)), Big(0))
      .div(fees.length);

    const apy = dpy.mul(365); // this is a conservative calculation, a perfect one would be exponential, but it would be unrealistic
    // setApy(apy.mul(100));

    const boostRewards = fromNano(boosts[0].rewardPerDay); // in TON;

    const getTONPrice = (index: number, amount: string) => {
      const asset: string = pool.assets[index].split(":")[1];

      // todo: do not hardcode tokens
      const assetKey =
        asset === "EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs"
          ? "USD"
          : "tsTON";

      return currency.convert(fromNano(amount), "TON", assetKey);
    };

    const fullReserve = reserves
      .map((amount, i) => getTONPrice(i, amount))
      .reduce((prev, value) => prev.add(value), Big(0));

    const rewardDpy = fullReserve.gt(0)
      ? boostRewards.div(fullReserve)
      : Big(0);
    const rewardApy = rewardDpy.mul(365).div(reserves.length);

    setApy(apy.add(rewardApy).mul(100));
  };
};

export const useUpdateEarnApy = () => {
  useUpdateEvaaApy();
  const updateStonApy = useUpdateStonApy();
  const updateDedustApy = useUpdateDedustApy();
  const currency = useCurrency();

  useInterval(
    () => {
      updateStonApy();
      updateDedustApy();
    },
    3600 * 1000,
    [currency.loaded],
    true,
  );
};
